import { AccountCircle } from '@mui/icons-material';
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import HelpOutline from '@mui/icons-material/HelpOutline';
import Logout from '@mui/icons-material/Logout';
import Translate from '@mui/icons-material/Translate';
import {
    Box,
    Divider,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Popover,
    Switch,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    LanguageList,
    languageList,
} from '../header/language-section/language-list';

import { SidebarDivider } from './sidebar-divider';
import { SidebarItem } from './sidebar-item';

import { useUserPermissions } from '@/hooks/use-user-permissions';
import { useColorModeStore } from '@/store/color-mode-store';
import { signOutToLogin } from '@/utils/session/sign-out-to-login';

const TELMA_GUIDE_URL = '/telma-guide';

// Portal above DFE's body-portaled JSON editor overlay (z-index 20000).
const POPUP_Z_INDEX = 21000;

const popupPlacement = {
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    transformOrigin: { vertical: 'bottom', horizontal: 'left' },
    slotProps: { paper: { sx: { ml: 1 } } },
    sx: { zIndex: POPUP_Z_INDEX },
} as const;

type FooterItemProps = { isNarrowBar: boolean };

const usePopupAnchor = () => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    return {
        anchorEl,
        isOpen: !!anchorEl,
        open: (event: MouseEvent<HTMLElement>) =>
            setAnchorEl(event.currentTarget),
        close: () => setAnchorEl(null),
    };
};

const DocsItem = ({ isNarrowBar }: FooterItemProps) => {
    const { t } = useTranslation();
    const router = useRouter();

    return (
        <SidebarItem
            icon={<HelpOutline />}
            label={t('documentation')}
            isNarrowBar={isNarrowBar}
            selected={router.pathname.startsWith(TELMA_GUIDE_URL)}
            onClick={() => router.push(TELMA_GUIDE_URL)}
            data-testid='sidebar-docs'
        />
    );
};

const LanguageItem = ({ isNarrowBar }: FooterItemProps) => {
    const { t, i18n } = useTranslation();
    const popup = usePopupAnchor();
    const currentLanguageName = languageList.find(
        (language) => language.value === i18n.language,
    )?.name;

    return (
        <>
            <SidebarItem
                icon={<Translate />}
                label={t('language')}
                secondaryLabel={currentLanguageName}
                isNarrowBar={isNarrowBar}
                hasPopup
                expanded={popup.isOpen}
                onClick={popup.open}
                data-testid='sidebar-language'
            />
            <Popover
                open={popup.isOpen}
                anchorEl={popup.anchorEl}
                onClose={popup.close}
                {...popupPlacement}
            >
                <LanguageList onSelect={popup.close} />
            </Popover>
        </>
    );
};

const DarkModeMenuItem = () => {
    const { t } = useTranslation();
    const { isHgcConnex } = useUserPermissions();
    const isDarkMode = useColorModeStore((state) => state.colorMode === 'dark');
    const toggleColorMode = useColorModeStore((state) => state.toggleColorMode);

    if (isHgcConnex) {
        return null;
    }

    return (
        <MenuItem onClick={toggleColorMode} data-testid='sidebar-dark-mode'>
            <ListItemIcon>
                <DarkModeOutlined fontSize='small' />
            </ListItemIcon>
            <ListItemText
                primary={`${t('dark_mode')} (${t('experimental')})`}
            />
            <Switch
                size='small'
                edge='end'
                checked={isDarkMode}
                tabIndex={-1}
                inputProps={{ 'aria-label': t('dark_mode') }}
            />
        </MenuItem>
    );
};

const ProfileItem = ({ isNarrowBar }: FooterItemProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { data: session } = useSession();
    const popup = usePopupAnchor();

    const userName = session?.user?.name ?? '';
    const roleLabel = session?.user?.roles?.isTester
        ? t('role_tester')
        : t('role_project_admin');

    return (
        <>
            <SidebarItem
                icon={<AccountCircle />}
                label={userName || t('user_settings')}
                secondaryLabel={roleLabel}
                isNarrowBar={isNarrowBar}
                hasPopup
                expanded={popup.isOpen}
                onClick={popup.open}
                data-testid='sidebar-profile'
            />
            <Menu
                open={popup.isOpen}
                anchorEl={popup.anchorEl}
                onClose={popup.close}
                {...popupPlacement}
            >
                <Box sx={{ px: 2, py: 1, minWidth: 220 }}>
                    <Typography variant='h5'>
                        {t('good_day')}, {userName}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                        {roleLabel}
                    </Typography>
                </Box>
                <Divider
                    sx={{
                        borderColor: alpha(theme.palette.common.white, 0.12),
                        my: 1,
                    }}
                />
                <DarkModeMenuItem />
                <MenuItem
                    onClick={() => signOutToLogin(!!session)}
                    data-testid='sidebar-logout'
                >
                    <ListItemIcon>
                        <Logout fontSize='small' />
                    </ListItemIcon>
                    {t('logout')}
                </MenuItem>
            </Menu>
        </>
    );
};

export const SidebarFooter = ({ isNarrowBar }: FooterItemProps) => (
    <>
        <DocsItem isNarrowBar={isNarrowBar} />
        <SidebarDivider />
        <LanguageItem isNarrowBar={isNarrowBar} />
        <ProfileItem isNarrowBar={isNarrowBar} />
    </>
);
